import { useCallback } from 'react';
import { backupboxRemoval, suriaHomeBonus, perBatteryPromo } from '../utils/rscPromos';

const CC_FACTORS_RSC = { 12: 0.955, 24: 0.935, 36: 0.925, 60: 0.9 };
export const roundUp10 = (v) => Math.ceil((Number(v) || 0) / 10) * 10;
export const ccFromCash = (cash, term) => roundUp10((Number(cash) || 0) / (CC_FACTORS_RSC[term] || 0.925));

// ctx = { phaseMode, promoBackupbox, promoFirst640, promoPerBatt, promoGovRebate, invKwOf, calcGovRebate }
// The engine is discount-agnostic: callers pass their own `disc` object
// ({ promoAmt, discAmt, discPct }) to getPrice/buildBreakdown. The component keeps its
// own getPromoDisc (which bundles the Bill-tab discount) and just calls computePromoAmt here.
export default function useRscEngine(ctx) {
    const { phaseMode, promoBackupbox, promoFirst640, promoPerBatt, promoGovRebate, invKwOf, calcGovRebate } = ctx;

    const getBasePrice = useCallback((pkg, field) => {
        if (phaseMode === '3phase') return field === 'cash' ? (pkg.totalPrice3p || pkg.totalPrice) : (pkg.ccPrice3p || pkg.ccPrice);
        return field === 'cash' ? pkg.totalPrice : pkg.ccPrice;
    }, [phaseMode]);

    // Stacked price application — promo (RM total) applied first, then a
    // single manual discount (RM amount OR %) applied on top of the residual.
    // disc shape: { promoAmt, discAmt, discPct }
    // systemBase (optional): for the AI BESS 360 tab, the % discount is meant
    // to apply ONLY to the panel/inverter portion (excludes battery + add-ons).
    // When systemBase is provided, discPct is computed against it instead of
    // afterPromo. Callers that don't care (Bill tab legacy path) omit it.
    // A NEGATIVE amount or percentage is a MARK-UP, and has to travel the same path as a
    // discount so the price, the breakdown and everything derived from the price (ROI,
    // payback, the quotation) all move together. The tests were `> 0`, so a negative was
    // not "applied as a markup" — it fell through every branch and returned the price
    // untouched, silently. The field accepted it and nothing happened.
    // (user 2026-10-14: "we put negative amount, the price stil no markup")
    const applyDisc = (base, disc, systemBase = null) => {
        const promoAmt = disc?.promoAmt || 0;
        const afterPromo = Math.max(0, base - promoAmt);
        // Clamped at 0 only on the discount side; subtracting a negative raises the price,
        // which is the point.
        if (disc?.discAmt) return Math.max(0, afterPromo - disc.discAmt);
        if (disc?.discPct) {
            const pctBase = systemBase != null ? systemBase : afterPromo;
            const pctAmt = Math.round(pctBase * disc.discPct / 100);
            return Math.max(0, afterPromo - pctAmt);
        }
        return afterPromo;
    };
    const computePromoAmt = useCallback((pkg) => {
        const is3p = phaseMode === '3phase';
        const hasBatt = Number(pkg?.battQty ?? 0) > 0;
        // Box is "removed" only when there's no battery AND removal is ticked.
        // A battery always keeps the box; a no-battery option keeps it too unless ticked.
        const removed = promoBackupbox && !hasBatt;
        let amt = 0;
        // All promo amounts come from utils/rscPromos (shared with create-sales) so
        // the RSC card and the created sale can never disagree. (user 2026-08-07)
        if (removed) amt += backupboxRemoval(is3p);
        if (promoFirst640) amt += suriaHomeBonus(removed, is3p);
        if (promoPerBatt && Number(pkg?.battKwhEach ?? 0) === 16) amt += perBatteryPromo(pkg?.battQty);
        // Gov rebate: RM600 × inverter kW (NOT panel kWp), capped RM3,000.
        if (promoGovRebate) amt += calcGovRebate(invKwOf(pkg));
        return amt;
    }, [phaseMode, promoBackupbox, promoFirst640, promoPerBatt, promoGovRebate, invKwOf]);
    const getPrice = useCallback((pkg, field, disc) => {
        const base = getBasePrice(pkg, field);
        if (field === 'cash' && base) {
            // % discount applies to system size (panel + inverter) AFTER promos —
            // excludes battery + add-ons. Battery promo (perBatt) reduces the
            // battery, not the system, so it's left out of the % base.
            const battCost = (Number(pkg.battUnitPrice) || 0) * (Number(pkg.battQty) || 0);
            const systemBase = Math.max(0, base - battCost);
            const promoAmt = disc?.promoAmt || 0;
            // Only the SYSTEM price promos (Backupbox + FIRST640) reduce the % base.
            // Per-battery promo reduces the battery; the Gov rebate is claimed from
            // TNB AFTER install (not a point-of-sale price cut) — so exclude both.
            const battPromo = (promoPerBatt && Number(pkg?.battKwhEach ?? 0) === 16) ? perBatteryPromo(pkg?.battQty) : 0;
            const govPromo = promoGovRebate ? calcGovRebate(invKwOf(pkg)) : 0;
            const systemAfterPromo = Math.max(0, systemBase - Math.max(0, promoAmt - battPromo - govPromo));
            return applyDisc(base, disc, systemAfterPromo);
        }
        return base;
    }, [getBasePrice, promoPerBatt, promoGovRebate, invKwOf]);
    // The base a per-card % discount applies to = system size after the system
    // promos (Backupbox + FIRST640), excluding battery, add-ons, per-battery promo
    // and the gov rebate. Used to convert between the RM and % discount fields.
    const cardPctBase = useCallback((pkg) => {
        const base = getBasePrice(pkg, 'cash');
        if (!base) return 0;
        const battCost = (Number(pkg.battUnitPrice) || 0) * (Number(pkg.battQty) || 0);
        const systemBase = Math.max(0, base - battCost);
        const promoAmt = computePromoAmt(pkg);
        const battPromo = (promoPerBatt && Number(pkg?.battKwhEach ?? 0) === 16) ? perBatteryPromo(pkg?.battQty) : 0;
        const govPromo = promoGovRebate ? calcGovRebate(invKwOf(pkg)) : 0;
        return Math.max(0, systemBase - Math.max(0, promoAmt - battPromo - govPromo));
    }, [getBasePrice, computePromoAmt, promoPerBatt, promoGovRebate, invKwOf]);

    // Build the line-by-line breakdown shown in the formula box. Returns null
    // if no promo and no manual discount apply (nothing to break down).
    const buildBreakdown = useCallback((pkg, disc, field = 'cash') => {
        if (!pkg) return null;
        const base = getBasePrice(pkg, field);
        if (!base) return null;
        const is3p = phaseMode === '3phase';
        const lines = [];
        const hasBatt = Number(pkg?.battQty ?? 0) > 0;
        const removed = promoBackupbox && !hasBatt;
        if (removed) lines.push({ key: 'backupbox', label: 'Backupbox Removal', amt: backupboxRemoval(is3p) });
        if (promoFirst640)  lines.push({ key: 'first640',  label: 'SuRIA Home Bonus Promo', amt: suriaHomeBonus(removed, is3p) });
        if (promoPerBatt && Number(pkg?.battKwhEach ?? 0) === 16) {
            const qty = pkg?.battQty ?? 0;
            lines.push({ key: 'perBatt', label: `Per Battery (${qty}×16kWh)`, amt: perBatteryPromo(qty) });
        }
        if (promoGovRebate) {
            const kw = invKwOf(pkg);
            const r = calcGovRebate(kw);
            if (r > 0) lines.push({ key: 'govRebate', label: `SuRIA Home Rebate (${kw}kW)`, amt: r });
        }
        const promoTotal = lines.reduce((s, l) => s + l.amt, 0);
        const afterPromo = Math.max(0, base - promoTotal);
        // System size (panel + inverter) vs battery — surface separately so the
        // breakdown can show what the % discount actually applies to.
        const battCost = (Number(pkg.battUnitPrice) || 0) * (Number(pkg.battQty) || 0);
        const systemBase = Math.max(0, base - battCost);
        // % applies to the system AFTER promos (excl battery + add-ons). The
        // per-battery promo reduces the battery, and the Gov rebate is claimed from
        // TNB AFTER install (not a price cut) — so neither reduces the % base.
        const battPromo = (lines.find(l => l.key === 'perBatt') || {}).amt || 0;
        const govPromo = (lines.find(l => l.key === 'govRebate') || {}).amt || 0;
        const systemAfterPromo = Math.max(0, systemBase - Math.max(0, promoTotal - battPromo - govPromo));
        let discLine = null, final = afterPromo;
        // Negative = a mark-up, and it must appear in the breakdown rather than being
        // dropped: a price that moved with nothing explaining it is worse than no line.
        // Math.min caps a DISCOUNT at the remaining price; a markup must not be capped,
        // so it is only applied on the positive side. (user 2026-10-14)
        if (disc.discAmt) {
            const markup = disc.discAmt < 0;
            const amt = markup ? disc.discAmt : Math.min(disc.discAmt, afterPromo);
            discLine = {
                // Internal only — this line never reaches the quotation, where a
                // mark-up would be a sensitive thing to print. (user 2026-10-14)
                label: markup ? 'Transportation' : 'Discount',
                amt,
                suffix: `RM ${Math.abs(disc.discAmt).toLocaleString()}`,
                appliesTo: 'system',
            };
            final = afterPromo - amt;
        } else if (disc.discPct) {
            // % applies only to system size (panel + inverter) after promos, not battery.
            const markup = disc.discPct < 0;
            const amt = Math.round(systemAfterPromo * disc.discPct / 100);
            discLine = {
                label: markup ? 'Transportation' : 'System Discount',
                amt,
                suffix: `${Math.abs(disc.discPct)}% × system`,
                appliesTo: 'system',
            };
            final = Math.max(0, afterPromo - amt);
        }
        if (lines.length === 0 && !discLine) return null;
        return { base, systemBase, battCost, lines, promoTotal, afterPromo, discLine, final };
    }, [getBasePrice, phaseMode, promoBackupbox, promoFirst640, promoPerBatt, promoGovRebate, invKwOf]);

    return { ccFromCash, roundUp10, applyDisc, getBasePrice, getPrice, computePromoAmt, cardPctBase, buildBreakdown };
}
